import { useEffect } from 'react';

/**
 * Adds `<meta name="robots" content="noindex">` on mount and undoes it on unmount,
 * so this page (and only this page) is excluded from search-engine indexing.
 *
 * Used by the demo microsites (`/weboldal-keszites/pelda/*`): they are publicly
 * routed and set a real title/description, but contain FICTIONAL sample content
 * (invented businesses) that must not show up in search results. Removing the tag
 * on unmount means navigating to a real page restores indexability — important in
 * this SPA, where the document `<head>` is shared across routes.
 *
 * Create-vs-restore logic mirrors useDocumentMeta: if a robots tag already exists
 * at mount, its previous content is restored on unmount; if we created it, it is
 * removed.
 *
 * THIS HOOK IS NOT THE ENFORCEMENT POINT — it is half of a pair.
 *
 * It only takes effect after React hydrates, so a crawler that does not execute
 * JS never sees the tag, while the demo URLs are publicly linked from the pricing
 * cards and can be discovered from that anchor text alone. (An earlier version of
 * this comment claimed non-JS crawlers "only ever see the empty SPA shell, so
 * there is nothing to index" — that is not an enforcement guarantee: the URL
 * itself is still indexable.)
 *
 * The actual guarantee is the `X-Robots-Tag: noindex, nofollow` response header
 * on `/weboldal-keszites/pelda/*` in `staticwebapp.config.json`, which is served
 * with the response and holds regardless of JS execution.
 *
 * Keep BOTH: the header covers direct hits and non-JS crawlers; this hook covers
 * client-side SPA navigation, which fires no new HTTP request and therefore gets
 * no header. Neither alone is sufficient.
 */
function useNoIndex() {
  useEffect(() => {
    let metaEl = document.querySelector('meta[name="robots"]');
    const previousContent = metaEl ? metaEl.getAttribute('content') : null;
    let wasCreated = false;

    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'robots');
      document.head.appendChild(metaEl);
      wasCreated = true;
    }
    metaEl.setAttribute('content', 'noindex');

    return () => {
      if (wasCreated) {
        metaEl.parentNode?.removeChild(metaEl);
      } else if (previousContent === null) {
        // Pre-existing tag had no content attribute — drop the one we added,
        // rather than leaving "noindex" stuck on it.
        metaEl.removeAttribute('content');
      } else {
        metaEl.setAttribute('content', previousContent);
      }
    };
  }, []);
}

export default useNoIndex;
